import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../../config';

// Role dropdown (October 2026, see routes/jobRoles.js). Empty value = Driver, the default. Admins
// get "+ Add a role…" at the bottom to create one in place. Used by the add/edit form and bulk upload.
let cache = null;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export function useJobRoles() {
  const [state, setState] = useState(cache || { roles: [], canAdd: false });
  useEffect(() => {
    if (cache) return;
    axios.get(`${API_URL}/api/job-roles`, auth())
      .then(r => { cache = r.data; setState(r.data); })
      .catch(() => {});
  }, []);
  const add = async name => {
    const r = await axios.post(`${API_URL}/api/job-roles`, { name }, auth());
    const roles = [...state.roles.filter(x => x.id !== r.data.id), r.data].sort((a, b) => a.name.localeCompare(b.name));
    cache = { ...state, roles };
    setState(cache);
    return r.data.name;
  };
  return { ...state, add };
}

export default function RoleField({ value, onChange, label = 'Role', className = 'form-group', disabled = false }) {
  const { roles, canAdd, add } = useJobRoles();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  // Keep a saved role visible even if it isn't in the list yet (list still loading).
  const options = value && !roles.some(r => r.name === value) ? [...roles, { id: 'cur', name: value }] : roles;

  const save = async () => {
    setError('');
    try {
      const saved = await add(name.trim());
      onChange(saved);
      setAdding(false);
      setName('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not add the role.');
    }
  };

  return (
    <div className={className}>
      <label>{label}</label>
      {adding ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Helper" maxLength={40}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') setAdding(false); }} />
          <button type="button" className="mtn-btn" onClick={save} disabled={!name.trim()}>Add</button>
          <button type="button" className="mtn-btn" onClick={() => { setAdding(false); setError(''); }}>Cancel</button>
        </div>
      ) : (
        <select value={value || ''} disabled={disabled} onChange={e => (e.target.value === '__add' ? setAdding(true) : onChange(e.target.value))}>
          <option value="">Driver</option>
          {options.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
          {canAdd && <option value="__add">+ Add a role…</option>}
        </select>
      )}
      {error && <small style={{ color: 'var(--red)' }}>{error}</small>}
      {value && !adding && <small style={{ color: 'var(--muted)' }}>Not AI rated. Texts and emails say "{value}".</small>}
    </div>
  );
}

// Small label shown next to a non-driver's name in lists. Drivers show nothing.
export function RoleBadge({ role }) {
  if (!role) return null;
  return <span className="role-badge">{role}</span>;
}
